import { createReducer, WritableDraft } from "@reduxjs/toolkit";
import { APIModule } from "../../../server/types/index.js";
import { ModulesBySiteModuleType, ModuleType } from "../../types/index.js";
import { creatableGlobalModules, removeFromArray } from "../../utils/utils.js";
import { deleteModuleTranslationStart } from "./actions.js";
import { getSiteModules, postModule } from "./thunks.js";

export const initialState: ModulesBySiteModuleType = {};

const getSiteModulesSuccessInternal = (
  modules: APIModule[],
): ModulesBySiteModuleType =>
  modules.reduce<ModulesBySiteModuleType>((newState, module) => {
    // Don’t include submodules
    if (module.parentId !== null) return newState;

    const clone = { ...newState };
    clone[module.type] = [...(clone[module.type] ?? []), module.id];

    return clone;
  }, {});

const postModuleStartInternal = ({
  state,
  moduleId,
  moduleType,
  pageId,
}: {
  state: WritableDraft<ModulesBySiteModuleType>;
  moduleId: string;
  moduleType: ModuleType;
  pageId: string | null;
}) => {
  const isTopLevelGlobal =
    pageId === null && creatableGlobalModules.includes(moduleType);
  if (!isTopLevelGlobal) return;

  const currentModuleIds = state[moduleType] ?? [];
  state[moduleType] = [...currentModuleIds, moduleId];
};

const deleteModuleTranslationInternal = ({
  state,
  moduleType,
  isUntranslated,
  moduleId,
}: {
  state: WritableDraft<ModulesBySiteModuleType>;
  moduleType: ModuleType;
  isUntranslated: boolean;
  moduleId: string;
}) => {
  if (!isUntranslated || !creatableGlobalModules.includes(moduleType)) {
    return;
  }

  const newModuleIds = removeFromArray<string>(
    state[moduleType] ?? [],
    moduleId,
  );

  if (newModuleIds.length) {
    state[moduleType] = newModuleIds;
    return;
  }

  delete state[moduleType];
};

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(getSiteModules.fulfilled, (_state, action) =>
    getSiteModulesSuccessInternal(action.payload),
  );

  builder.addCase(postModule.pending, (state, action) => {
    const { moduleId, moduleType, pageId } = action.meta.arg;
    postModuleStartInternal({ state, moduleId, moduleType, pageId });
  });

  builder.addCase(deleteModuleTranslationStart, (state, action) => {
    deleteModuleTranslationInternal({
      state,
      isUntranslated: action.payload.deleteAllTranslations,
      ...action.payload,
    });
  });
});

export default reducer;
